import { uiT } from './uiTranslate';

/**
 * Hodnota nastavenia pre modul a kľúč.
 * @param {Array} moduleSettings
 * @param {string} module
 * @param {string} key
 * @param {string} [defaultValue='1']
 * @returns {string}
 */
export function getModuleSettingValue(moduleSettings, module, key, defaultValue = '1') {
  const row = (moduleSettings || []).find(s => s.module === module && s.key === key);
  const v = row ? (row.value ?? defaultValue) : defaultValue;
  if (v == null || v === '') return defaultValue;
  // Pre 0/1 vždy vráť string '0'/'1', aby zoznamy a SelectButton mali konzistentný tvar
  return (v === 0 || v === '0') ? '0' : (v === 1 || v === '1') ? '1' : String(v);
}

/** Globálna mena z Jednotiek (napr. €, CZK, $). */
export function getCurrency(moduleSettings) {
  return getModuleSettingValue(moduleSettings || [], 'global', 'currency', '€');
}

/** Globálne časové pásmo (napr. Europe/Bratislava). */
export function getTimezone(moduleSettings) {
  return getModuleSettingValue(moduleSettings || [], 'global', 'timezone', 'Europe/Bratislava');
}

/**
 * Lokalita pre počasie (lakovňa). Ak je nastavené weather_location (mesto/adresa), vráti { query } – API nájde najbližší bod.
 * Inak vráti { lat, lon } zo súradníc (fallback Košice).
 */
export function getWeatherLocationForApi(moduleSettings) {
  const ms = moduleSettings || [];
  const place = (getModuleSettingValue(ms, 'global', 'weather_location', '') || '').trim();
  if (place) {
    return { query: place };
  }
  const latStr = getModuleSettingValue(ms, 'global', 'company_lat', '48.559885');
  const lonStr = getModuleSettingValue(ms, 'global', 'company_lon', '21.266370');
  const lat = parseFloat(latStr);
  const lon = parseFloat(lonStr);
  return {
    lat: Number.isFinite(lat) ? lat : 48.559885,
    lon: Number.isFinite(lon) ? lon : 21.266370,
  };
}

/** @deprecated Použiť getWeatherLocationForApi. Zachované pre spätnú kompatibilitu. */
export function getCompanyWeatherLocation(moduleSettings) {
  const loc = getWeatherLocationForApi(moduleSettings);
  return 'query' in loc ? { lat: 48.559885, lon: 21.266370 } : loc;
}

/** Globálna jednotka teploty z Jednotiek (napr. °C, K, °F). */
export function getTemperature(moduleSettings) {
  return getModuleSettingValue(moduleSettings || [], 'global', 'temperature', '°C');
}

/** Globálny formát dátumu (Luxon tokeny, napr. dd.MM.yyyy). */
export function getDateFormat(moduleSettings) {
  return getModuleSettingValue(moduleSettings || [], 'global', 'date_format', 'dd.MM.yyyy');
}

/** Jednotka času (dni) z Jednotiek – pre trvanie, termíny (napr. dní, dní). */
export function getTimeUnitDays(moduleSettings) {
  return getModuleSettingValue(moduleSettings || [], 'global', 'time_unit_days', 'dní');
}

/** Či zobrazovať deaktivované v zoznamoch (1 = áno, 0 = nie). */
export function showDeactivatedInLists(moduleSettings, module) {
  return getModuleSettingValue(moduleSettings, module, 'show_deactivated_in_lists', '1') === '1';
}

/**
 * Training: či zobrazovať neaktívnych zamestnancov (date_of_departure) v zoznamoch.
 * Default 0 = skryť (migration 2026_03_08).
 */
export function showInactiveEmployeesInLists(moduleSettings) {
  return getModuleSettingValue(moduleSettings || [], 'training', 'show_inactive_employees_in_lists', '0') === '1';
}

/** Stroje vyradené zo zoznamu strojov (machines.validity = 0). */
export function showExcludedMachinesInLists(moduleSettings) {
  return getModuleSettingValue(moduleSettings || [], 'maintenance', 'show_excluded_machines', '0') === '1';
}

/** Vyradené servisné firmy (companies.validity != 1, type=maintenance). Default 0. */
export function showExcludedServiceCompaniesInLists(moduleSettings) {
  return getModuleSettingValue(moduleSettings || [], 'maintenance', 'show_excluded_service_companies', '0') === '1';
}

/** Hodnota nastavenia údržby (maintenance) – napr. farby indikátora a grafov. */
export function getMaintenanceSetting(moduleSettings, key, defaultValue = '') {
  return getModuleSettingValue(moduleSettings || [], 'maintenance', key, defaultValue);
}

/** Spoločné mapovanie názov farby → hex (pre grafy a indikátory). Rovnaké možnosti ako SEMAFOR_COLOR_OPTIONS. */
export const COLOR_NAME_TO_HEX = {
  red: '#dc2626',
  orange: '#ca8a04',
  yellow: '#eab308',
  green: '#16a34a',
  gray: '#6b7280',
  grey: '#6b7280',
  blue: '#2563eb',
  purple: '#9333ea',
  black: '#171717',
  s: '#6b7280',
  siva: '#6b7280',
  'sivá': '#6b7280',
  g: '#16a34a',
  zelena: '#16a34a',
  'zelená': '#16a34a',
  c: '#dc2626',
  cervena: '#dc2626',
  'červená': '#dc2626',
  o: '#ca8a04',
  oranzova: '#ca8a04',
  'oranžová': '#ca8a04',
  z: '#eab308',
  zlta: '#eab308',
  'žltá': '#eab308',
  m: '#2563eb',
  modra: '#2563eb',
  'modrá': '#2563eb',
};

function expandShortHex(threeCharHex) {
  const h = threeCharHex.toLowerCase();
  return `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`;
}

/** Ak je v hodnota názov farby, vráti hex; ak už je hex (#...), vráti ako je; inak defaultHex. */
export function colorNameOrHexToHex(value, defaultHex) {
  let v = String(value ?? '').trim();
  if (!v) return defaultHex;

  while (v.startsWith('##')) {
    v = v.slice(1);
  }

  let m = v.match(/^#?([0-9A-Fa-f]{6})$/i);
  if (m) return `#${m[1].toLowerCase()}`;

  m = v.match(/^#?([0-9A-Fa-f]{3})$/i);
  if (m) return expandShortHex(m[1]);

  m = v.match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i);
  if (m) {
    const hex = [m[1], m[2], m[3]]
      .map((n) => Math.min(255, Math.max(0, parseInt(n, 10))).toString(16).padStart(2, '0'))
      .join('');
    return `#${hex}`;
  }

  const key = v.toLowerCase();
  return COLOR_NAME_TO_HEX[key] ?? defaultHex;
}

const SERVICE_INDICATOR_DEFAULT_NAMES = { red: 'red', orange: 'orange', green: 'green' };
const SERVICE_INDICATOR_DEFAULT_HEX = { red: '#dc2626', orange: '#ca8a04', green: '#16a34a' };
/** Farby indikátora stavu tikiet (po termíne / blíži sa / OK) – len pre tikety, nie pre stroj. Vracia hex. */
export function getServiceStatusIndicatorColors(moduleSettings) {
  const ms = moduleSettings || [];
  return {
    red: colorNameOrHexToHex(getMaintenanceSetting(ms, 'status_indicator_red', SERVICE_INDICATOR_DEFAULT_NAMES.red), SERVICE_INDICATOR_DEFAULT_HEX.red),
    orange: colorNameOrHexToHex(getMaintenanceSetting(ms, 'status_indicator_orange', SERVICE_INDICATOR_DEFAULT_NAMES.orange), SERVICE_INDICATOR_DEFAULT_HEX.orange),
    green: colorNameOrHexToHex(getMaintenanceSetting(ms, 'status_indicator_green', SERVICE_INDICATOR_DEFAULT_NAMES.green), SERVICE_INDICATOR_DEFAULT_HEX.green),
  };
}

/** Farby indikátora termínu CRM tiketov (Správa zákazníkov – Komunikácia). */
export function getCrmStatusIndicatorColors(moduleSettings) {
  const ms = moduleSettings || [];
  return {
    red: colorNameOrHexToHex(getModuleSettingValue(ms, 'crm', 'status_indicator_red', SERVICE_INDICATOR_DEFAULT_NAMES.red), SERVICE_INDICATOR_DEFAULT_HEX.red),
    orange: colorNameOrHexToHex(getModuleSettingValue(ms, 'crm', 'status_indicator_orange', SERVICE_INDICATOR_DEFAULT_NAMES.orange), SERVICE_INDICATOR_DEFAULT_HEX.orange),
    green: colorNameOrHexToHex(getModuleSettingValue(ms, 'crm', 'status_indicator_green', SERVICE_INDICATOR_DEFAULT_NAMES.green), SERVICE_INDICATOR_DEFAULT_HEX.green),
  };
}

const SERVICE_GRAPH_DEADLINE_DEFAULT_NAMES = ['red', 'orange', 'green'];
const SERVICE_GRAPH_DEADLINE_DEFAULT_HEX = ['#b91c1c', '#ca8a04', '#15803d'];
/** Farby grafu „Po termíne / Blíži sa / OK“ (doughnut). Vracia hex. */
export function getServiceGraphDeadlineColors(moduleSettings) {
  const ms = moduleSettings || [];
  return [0, 1, 2].map((i) =>
    colorNameOrHexToHex(
      getMaintenanceSetting(ms, ['graph_deadline_red', 'graph_deadline_orange', 'graph_deadline_green'][i], SERVICE_GRAPH_DEADLINE_DEFAULT_NAMES[i]),
      SERVICE_GRAPH_DEADLINE_DEFAULT_HEX[i]
    )
  );
}

const SERVICE_GRAPH_STATES_DEFAULT_NAMES = ['orange', 'blue', 'blue', 'green', 'blue', 'gray'];
const SERVICE_GRAPH_STATES_DEFAULT_HEX = ['#facc15', '#93c5fd', '#1d4ed8', '#16a34a', '#a855f7', '#9ca3af'];
/** Farby grafu stavov (Čaká na schválenie, Schválený, …). Vracia hex. */
export function getServiceGraphStatesColors(moduleSettings) {
  const ms = moduleSettings || [];
  const base = [1, 2, 3, 4, 5, 6].map((i) =>
    colorNameOrHexToHex(getMaintenanceSetting(ms, `graph_states_${i}`, SERVICE_GRAPH_STATES_DEFAULT_NAMES[i - 1]), SERVICE_GRAPH_STATES_DEFAULT_HEX[i - 1])
  );
  // 7th bucket = other_open (COMPPORT003) – neutral gray unless configured
  const other = colorNameOrHexToHex(
    getMaintenanceSetting(ms, 'graph_states_7', 'gray'),
    '#6b7280'
  );
  return [...base, other];
}

const SERVICE_GRAPH_PERIOD_DEFAULT_NAMES = ['blue', 'red', 'green'];
const SERVICE_GRAPH_PERIOD_DEFAULT_HEX = ['#2563eb', '#dc2626', '#16a34a'];
/** Farby grafu obdobia (Pribudnuté, Nevyriešené, Ukončené). Vracia hex. */
export function getServiceGraphPeriodColors(moduleSettings) {
  const ms = moduleSettings || [];
  return [1, 2, 3].map((i) =>
    colorNameOrHexToHex(getMaintenanceSetting(ms, `graph_period_${i}`, SERVICE_GRAPH_PERIOD_DEFAULT_NAMES[i - 1]), SERVICE_GRAPH_PERIOD_DEFAULT_HEX[i - 1])
  );
}

/** Hodnota nastavenia pre modul checklists. */
export function getChecklistSetting(moduleSettings, key, defaultValue = '') {
  return getModuleSettingValue(moduleSettings || [], 'checklists', key, defaultValue);
}

const CHECKLIST_GRAPH_STATUS_DEFAULT_NAMES = ['green', 'red', 'orange', 'gray'];
const CHECKLIST_GRAPH_STATUS_DEFAULT_HEX = ['#15803d', '#b91c1c', '#ca8a04', '#6b7280'];
/** Farby grafu stavov checklistov (OK, NOK, Rozpracovaný, Nevytvorený). Vracia hex. */
export function getChecklistGraphStatusColors(moduleSettings) {
  const ms = moduleSettings || [];
  return [1, 2, 3, 4].map((i) =>
    colorNameOrHexToHex(getChecklistSetting(ms, `graph_status_${i}`, CHECKLIST_GRAPH_STATUS_DEFAULT_NAMES[i - 1]), CHECKLIST_GRAPH_STATUS_DEFAULT_HEX[i - 1])
  );
}

const CHECKLIST_GRAPH_VYJADRENIA_DEFAULT_NAMES = ['green', 'red', 'gray'];
const CHECKLIST_GRAPH_VYJADRENIA_DEFAULT_HEX = ['#15803d', '#b91c1c', '#6b7280'];
/** Farby grafu vyjadrení (Otvorené, Otvorené po termíne, Uzavreté). Vracia hex. */
export function getChecklistGraphVyjadreniaColors(moduleSettings) {
  const ms = moduleSettings || [];
  return [1, 2, 3].map((i) =>
    colorNameOrHexToHex(getChecklistSetting(ms, `graph_vyjadrenia_${i}`, CHECKLIST_GRAPH_VYJADRENIA_DEFAULT_NAMES[i - 1]), CHECKLIST_GRAPH_VYJADRENIA_DEFAULT_HEX[i - 1])
  );
}

/** Spoločné možnosti farieb pre semafory a grafy (zoznamy, náhľad kvality). Aspoň 7 farieb pre 7 semaforových možností. */
export const SEMAFOR_COLOR_OPTIONS = [
  { get label() { return uiT('Common.Color.Red'); }, value: 'red' },
  { get label() { return uiT('Common.Color.Orange'); }, value: 'orange' },
  { get label() { return uiT('Common.Color.Yellow'); }, value: 'yellow' },
  { get label() { return uiT('Common.Color.Green'); }, value: 'green' },
  { get label() { return uiT('Common.Color.Gray'); }, value: 'gray' },
  { get label() { return uiT('Common.Color.Blue'); }, value: 'blue' },
  { get label() { return uiT('Common.Color.Purple'); }, value: 'purple' },
  { get label() { return uiT('Common.Color.Black'); }, value: 'black' },
];

/** Lokalizované možnosti farieb pre semafory a grafy. */
export function getSemaforColorOptions(t) {
  return [
    { label: t('Common.Color.Red'), value: 'red' },
    { label: t('Common.Color.Orange'), value: 'orange' },
    { label: t('Common.Color.Yellow'), value: 'yellow' },
    { label: t('Common.Color.Green'), value: 'green' },
    { label: t('Common.Color.Gray'), value: 'gray' },
    { label: t('Common.Color.Blue'), value: 'blue' },
    { label: t('Common.Color.Purple'), value: 'purple' },
    { label: t('Common.Color.Black'), value: 'black' },
  ];
}

/** Platné hodnoty pre výber farby (rovnaké ako optionValue v SEMAFOR_COLOR_OPTIONS). */
export const SEMAFOR_COLOR_VALUES = ['red', 'orange', 'yellow', 'green', 'gray', 'blue', 'purple', 'black'];

/** Tailwind trieda pre danú farbu semafora (kruh v zoznamoch). */
export function getSemaforColorClass(color) {
  const map = { red: 'bg-red-500', orange: 'bg-amber-500', yellow: 'bg-yellow-500', green: 'bg-green-500', gray: 'bg-gray-500', blue: 'bg-blue-500', purple: 'bg-purple-500', black: 'bg-zinc-800' };
  return map[color] || 'bg-amber-500';
}

/** Default farby pre OTK semafor (bucket -> farba) */
const OTK_SEMAFOR_DEFAULTS = {
  '0-': 'red',
  '15-': 'orange',
  '30-': 'orange',
  '45-': 'orange',
  '60-': 'orange',
  '61+': 'green',
  '30+': 'green',
  '60+': 'green',
  'DEA': 'gray',
};

/** Mapovanie bucket key (0-, 15-, ...) na kľúč v module_settings */
const OTK_SEMAFOR_KEY_MAP = {
  '0-': 'otk_semafor_0',
  '15-': 'otk_semafor_15',
  '30-': 'otk_semafor_30',
  '45-': 'otk_semafor_45',
  '60-': 'otk_semafor_60',
  '61+': 'otk_semafor_60_plus',
  '30+': 'otk_semafor_30_plus',
  '60+': 'otk_semafor_60_plus',
  'DEA': 'otk_semafor_dea',
};

/**
 * Vráti farbu semafora pre OTK stav expirácie (pre indikátor v zoznamoch).
 * @param {Array} moduleSettings
 * @param {string} bucket - jedna z: '0-', '15-', '30-', '45-', '60-', '30+', '60+', 'DEA'
 * @returns {string} red | orange | green | gray | blue | black
 */
export function getOtkSemaforColor(moduleSettings, bucket) {
  const settingKey = OTK_SEMAFOR_KEY_MAP[bucket];
  const defaultColor = OTK_SEMAFOR_DEFAULTS[bucket] || 'orange';
  if (!settingKey) return defaultColor;
  const value = getModuleSettingValue(moduleSettings, 'otk', settingKey, defaultColor);
  return SEMAFOR_COLOR_VALUES.includes(value) ? value : defaultColor;
}

/** Default farby pre kalibračný semafor. Buckety = presne tie, ktoré sa zobrazujú v zoznamoch kalibrácií. */
const CAL_SEMAFOR_DEFAULTS = {
  '0-': 'red',
  '15-': 'orange',
  '30-': 'orange',
  '45-': 'orange',
  '60-': 'orange',
  '61+': 'green',
  'DEA': 'gray',
};

/** Mapovanie bucket key pre kalibrácie na kľúč v module_settings (iba buckety reálne používané v zoznamoch). */
const CAL_SEMAFOR_KEY_MAP = {
  '0-': 'cal_semafor_0',
  '15-': 'cal_semafor_15',
  '30-': 'cal_semafor_30',
  '45-': 'cal_semafor_45',
  '60-': 'cal_semafor_60',
  '61+': 'cal_semafor_60_plus',
  'DEA': 'cal_semafor_dea',
};

/**
 * Vráti farbu semafora pre kalibračný stav expirácie (zoznamy, grafy).
 * @param {Array} moduleSettings
 * @param {string} bucket - jedna z: '0-', '15-', '30-', '45-', '60-', '30+', '60+', '61+'
 * @returns {string} red | orange | green | gray | blue | black
 */
export function getCalSemaforColor(moduleSettings, bucket) {
  const settingKey = CAL_SEMAFOR_KEY_MAP[bucket];
  const defaultColor = CAL_SEMAFOR_DEFAULTS[bucket] || 'orange';
  if (!settingKey) return defaultColor;
  const value = getModuleSettingValue(moduleSettings, 'calibration', settingKey, defaultColor);
  return SEMAFOR_COLOR_VALUES.includes(value) ? value : defaultColor;
}

/** Default farby pre školenia (semafor expirácie certifikátov). */
const TRNG_SEMAFOR_DEFAULTS = {
  '0-': 'red',
  '15-': 'orange',
  '30-': 'orange',
  '45-': 'orange',
  '60-': 'orange',
  '61+': 'green',
  'DEA': 'gray',
};

/** Mapovanie bucket key pre školenia na kľúč v module_settings. */
const TRNG_SEMAFOR_KEY_MAP = {
  '0-': 'trng_semafor_0',
  '15-': 'trng_semafor_15',
  '30-': 'trng_semafor_30',
  '45-': 'trng_semafor_45',
  '60-': 'trng_semafor_60',
  '61+': 'trng_semafor_60_plus',
  'DEA': 'trng_semafor_dea',
};

/**
 * Vráti farbu semafora pre školenia (stav expirácie certifikátov v zoznamoch).
 * @param {Array} moduleSettings
 * @param {string} bucket - jedna z: '0-', '15-', '30-', '45-', '60-', '61+', 'DEA'
 * @returns {string} red | orange | green | gray | blue | black
 */
export function getTrngSemaforColor(moduleSettings, bucket) {
  const settingKey = TRNG_SEMAFOR_KEY_MAP[bucket];
  const defaultColor = TRNG_SEMAFOR_DEFAULTS[bucket] || 'orange';
  if (!settingKey) return defaultColor;
  const value = getModuleSettingValue(moduleSettings, 'training', settingKey, defaultColor);
  return SEMAFOR_COLOR_VALUES.includes(value) ? value : defaultColor;
}

/** Default farby pre údržbu (semafor expirácie certifikátov servisných firiem). */
const MNTN_SEMAFOR_DEFAULTS = {
  '0-': 'red',
  '15-': 'orange',
  '30-': 'orange',
  '45-': 'orange',
  '60-': 'orange',
  '61+': 'green',
  'DEA': 'gray',
};

/** Mapovanie bucket key pre údržbu na kľúč v module_settings. */
const MNTN_SEMAFOR_KEY_MAP = {
  '0-': 'mntn_semafor_0',
  '15-': 'mntn_semafor_15',
  '30-': 'mntn_semafor_30',
  '45-': 'mntn_semafor_45',
  '60-': 'mntn_semafor_60',
  '61+': 'mntn_semafor_60_plus',
  'DEA': 'mntn_semafor_dea',
};

/**
 * Vráti farbu semafora pre údržbu (stav expirácie certifikátov v zoznamoch).
 * @param {Array} moduleSettings
 * @param {string} bucket - jedna z: '0-', '15-', '30-', '45-', '60-', '61+', 'DEA'
 * @returns {string} red | orange | green | gray | blue | black
 */
export function getMntnSemaforColor(moduleSettings, bucket) {
  const settingKey = MNTN_SEMAFOR_KEY_MAP[bucket];
  const defaultColor = MNTN_SEMAFOR_DEFAULTS[bucket] || 'orange';
  if (!settingKey) return defaultColor;
  const value = getModuleSettingValue(moduleSettings, 'maintenance', settingKey, defaultColor);
  return SEMAFOR_COLOR_VALUES.includes(value) ? value : defaultColor;
}

/** Default farby pre grafy v Náhľade údržby (4 segmenty: Po expirácii, 15 dní, 30 dní, OK). */
const MNTN_GRAPH_DEFAULT_NAMES = ['red', 'orange', 'orange', 'green'];
const MNTN_GRAPH_DEFAULT_HEX = ['#dc2626', '#ca8a04', '#eab308', '#16a34a'];
/** Farby pre grafy v Náhľade údržby (koláč: Po expirácii, 15 dní do exp., 30 dní do exp., OK). Vracia pole 4 hex. */
export function getMntnGraphColors(moduleSettings) {
  const ms = moduleSettings || [];
  return [0, 1, 2, 3].map((i) =>
    colorNameOrHexToHex(
      getMaintenanceSetting(ms, ['mntn_graph_0', 'mntn_graph_15', 'mntn_graph_30', 'mntn_graph_ok'][i], MNTN_GRAPH_DEFAULT_NAMES[i]),
      MNTN_GRAPH_DEFAULT_HEX[i]
    )
  );
}

/** Či zobrazovať deaktivované v notifikáciách. */
export function showDeactivatedInNotifications(moduleSettings, module) {
  return getModuleSettingValue(moduleSettings, module, 'show_deactivated_in_notifications', '1') === '1';
}

function hasDeactivation(item) {
  const d = item?.deactivation_date;
  return d != null && d !== '';
}

/**
 * Filtruje training_employees: ak showDeactivated je false, vráti len položky bez deactivation_date.
 * @param {Array} trainingEmployees
 * @param {boolean} showDeactivated
 */
export function filterTrainingEmployees(trainingEmployees, showDeactivated) {
  if (!Array.isArray(trainingEmployees)) return trainingEmployees;
  if (showDeactivated) return trainingEmployees;
  return trainingEmployees.filter(item => !hasDeactivation(item));
}

/**
 * Filtruje training_cards (employees s training_card): ak showDeactivated je false,
 * vráti len zamestnancov, ktorí majú aspoň jednu kartu bez deactivation_date.
 */
export function filterTrainingCards(trainingCards, showDeactivated) {
  if (!Array.isArray(trainingCards)) return trainingCards;
  if (showDeactivated) return trainingCards;
  return trainingCards.filter(emp => {
    const cards = emp?.training_card || [];
    return cards.some(c => !hasDeactivation(c));
  });
}

/**
 * Filtruje machine_cards: ak showDeactivated je false, vráti len stroje, ktorých machine_card nemá deactivation_date.
 */
export function filterMachineCards(machineCards, showDeactivated) {
  if (!Array.isArray(machineCards)) return machineCards;
  if (showDeactivated) return machineCards;
  return machineCards.filter(m => !hasDeactivation(m?.machine_card));
}

/** Vyradené meradlo má gauge_status === 'Vyradené'. */
function isGaugeDeactivated(gauge) {
  return (gauge?.gauge_status ?? '').trim() === 'Vyradené';
}

/**
 * Filtruje zoznam meradiel (gauges alebo gauge-riadky s gauge_status):
 * ak showDeactivated je false, vráti len meradlá, ktoré nie sú vyradené.
 * Používa sa v zoznamoch kalibrácií (podľa stavu, podľa meradla). Zoznam meradiel (GaugeList) zobrazuje vždy všetky.
 */
export function filterGauges(gauges, showDeactivated) {
  if (!Array.isArray(gauges)) return gauges;
  if (showDeactivated) return gauges;
  return gauges.filter(g => !isGaugeDeactivated(g));
}
